import React from 'react';
import dayjs from 'dayjs';
import { useLanguage } from '../contexts/LanguageContext';

const BlockScheduleList = ({
  blockSchedules,
  onEditBlockSchedule,
  onDeleteBlockSchedule,
  isLoading
}) => {
  const { t } = useLanguage();

  const formatDateTime = (dateTime) => {
    return dayjs(dateTime).format('YYYY/MM/DD HH:mm');
  };

  const getDuration = (from, until) => {
    const start = dayjs(from);
    const end = dayjs(until);
    const diff = end.diff(start, 'hour', true);

    if (diff < 1) {
      return `${Math.round(diff * 60)}m`;
    } else if (diff < 24) {
      return `${Math.round(diff)}h`;
    } else {
      const days = Math.floor(diff / 24);
      const hours = Math.round(diff % 24);
      return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
    }
  };

  const isActive = (blockSchedule) => {
    const now = dayjs();
    const from = dayjs(blockSchedule.block_from);
    const until = dayjs(blockSchedule.block_until);
    return now.isAfter(from) && now.isBefore(until);
  };

  const isUpcoming = (blockSchedule) => {
    const now = dayjs();
    const from = dayjs(blockSchedule.block_from);
    return from.isAfter(now);
  };

  const isExpired = (blockSchedule) => {
    const now = dayjs();
    const until = dayjs(blockSchedule.block_until);
    return until.isBefore(now);
  };

  const getSortPriority = (blockSchedule) => {
    if (!blockSchedule.active) {
      return 4; // Inactive - lowest priority
    }

    if (isActive(blockSchedule)) {
      return 1; // Active - highest priority
    } else if (isUpcoming(blockSchedule)) {
      return 2; // Upcoming - second priority
    } else if (isExpired(blockSchedule)) {
      return 3; // Expired - third priority
    }

    return 4; // Default fallback
  };

  const sortBlockSchedules = (schedules) => {
    return [...schedules].sort((a, b) => {
      const priorityA = getSortPriority(a);
      const priorityB = getSortPriority(b);

      // First sort by priority
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // Within same priority, sort by block_from date (earliest first)
      const dateA = dayjs(a.block_from);
      const dateB = dayjs(b.block_from);
      return dateA.diff(dateB);
    });
  };

  const getStatusBadge = (blockSchedule) => {
    if (!blockSchedule.active) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
          {t('blockScheduleList.inactive')}
        </span>
      );
    }

    if (isActive(blockSchedule)) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
          {t('blockScheduleList.active')}
        </span>
      );
    } else if (isUpcoming(blockSchedule)) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700">
          {t('blockScheduleList.upcoming')}
        </span>
      );
    } else if (isExpired(blockSchedule)) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
          {t('blockScheduleList.expired')}
        </span>
      );
    }

    return null;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-amber-500" />
      </div>
    );
  }

  // Hide past/expired and deactivated (inactive) block schedules
  const visibleSchedules = (blockSchedules || []).filter(
    (bs) => bs.active !== false && !isExpired(bs)
  );

  if (!blockSchedules || blockSchedules.length === 0) {
    return (
      <div className="text-center py-12 px-4 rounded-lg bg-gray-50/50 border border-gray-100">
        <p className="text-gray-500 text-sm">
          {t('blockScheduleList.noBlockSchedules', 'No holidays yet. Click "Add Holiday" to create one.')}
        </p>
      </div>
    );
  }

  if (visibleSchedules.length === 0) {
    return (
      <div className="text-center py-12 px-4 rounded-lg bg-gray-50/50 border border-gray-100">
        <p className="text-gray-500 text-sm">
          {t('blockScheduleList.noBlockSchedules', 'No holidays yet. Click "Add Holiday" to create one.')}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">{t('blockScheduleList.establishedBlockSchedules', 'Holidays')}</h4>
      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50/80 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">
          <div className="col-span-4">{t('blockScheduleList.reason', 'Reason')}</div>
          <div className="col-span-2">{t('blockScheduleList.scope', 'Scope')}</div>
          <div className="col-span-2">{t('blockScheduleList.blockFrom', 'From')}</div>
          <div className="col-span-2">{t('blockScheduleList.blockUntil', 'Until')}</div>
          {/* <div className="col-span-1">{t('blockScheduleList.duration', 'Duration')}</div> */}
          {/* <div className="col-span-1">{t('blockScheduleList.status', 'Status')}</div> */}
          <div className="col-span-2">{t('common.actions', 'Actions')}</div>
        </div>
        {sortBlockSchedules(visibleSchedules).map(blockSchedule => (
          <div key={blockSchedule.id} className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors duration-200">
            <div className="col-span-4 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate" title={blockSchedule.reason}>
                {blockSchedule.reason}
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-sm text-gray-600">
                {blockSchedule.doctor_id ? `Doctor ${blockSchedule.doctor_id}` : t('blockScheduleList.allDoctors', 'All Doctors')}
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-sm text-gray-600">{formatDateTime(blockSchedule.block_from)}</p>
            </div>
            <div className="col-span-2">
              <p className="text-sm text-gray-600">{formatDateTime(blockSchedule.block_until)}</p>
            </div>
            {/* <div className="col-span-1">
              <p className="text-sm text-gray-600">{getDuration(blockSchedule.block_from, blockSchedule.block_until)}</p>
            </div>
            <div className="col-span-1">
              {getStatusBadge(blockSchedule)}
            </div> */}
            <div className="col-span-2">
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => onEditBlockSchedule(blockSchedule)}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 transition-colors"
                >
                  {t('common.edit', 'Edit')}
                </button>
                <button
                  type="button"
                  onClick={() => onDeleteBlockSchedule(blockSchedule)}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 transition-colors"
                >
                  {t('common.delete', 'Delete')}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BlockScheduleList;
